import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { UserRolePermissions, AccessLevel } from '@/types/permissions';
import { useTimesheet } from './TimesheetContext';
import { useDataSource } from './DataSourceContext';
import { apiClient } from '@/services/apiClient';

// Storage key for employee role assignments in demo mode
const EMPLOYEE_ROLE_ASSIGNMENT_KEY = 'employeeRoleAssignments';

// Helper to get employee's assigned role ID from localStorage (demo mode)
function getEmployeeRoleIdFromStorage(employeeId: number): number | null {
  try {
    const stored = localStorage.getItem(EMPLOYEE_ROLE_ASSIGNMENT_KEY);
    if (stored) {
      const assignments = JSON.parse(stored) as Record<string, number>;
      return assignments[String(employeeId)] ?? null;
    }
  } catch (error) {
    console.error('[PermissionContext] Error loading employee role assignment:', error);
  }
  return null;
}

// Helper to load role permissions from localStorage (demo mode)
function loadRolePermissionsFromStorage(roleId: number): UserRolePermissions | null {
  try {
    const permissionsKey = `userRolePermissions_${roleId}`;
    const stored = localStorage.getItem(permissionsKey);
    if (stored) {
      return JSON.parse(stored) as UserRolePermissions;
    }
  } catch (error) {
    console.error('[PermissionContext] Error loading role permissions:', error);
  }
  return null;
}

// Helper to get role name from localStorage (demo mode)
function getRoleNameFromStorage(roleId: number): string | null {
  try {
    const stored = localStorage.getItem('userRoles');
    if (stored) {
      const roles = JSON.parse(stored) as Array<{ id: number; name: string }>;
      const role = roles.find(r => r.id === roleId);
      return role?.name ?? null;
    }
  } catch (error) {
    console.error('[PermissionContext] Error loading role name:', error);
  }
  return null;
}

interface PermissionContextType {
  permissions: UserRolePermissions | null;
  userRoleId: number | null;
  userRoleName: string | null;
  isLoading: boolean;
  
  // Timesheet permissions
  canViewTimesheet: (targetEmployeeId: number) => boolean;
  canEditTimesheet: (targetEmployeeId: number) => boolean;
  canDeleteTimesheet: () => boolean;
  getTimesheetViewAccessLevel: () => AccessLevel;
  getTimesheetEditAccessLevel: () => AccessLevel;
  
  // Approval permissions
  canViewApprovalDashboard: () => boolean;
  canViewApprovals: () => boolean;
  canApproveReject: () => boolean;
  canBulkApprove: () => boolean;
  canDelegateApproval: () => boolean;
  
  // Report permissions
  canViewReport: (reportType: 'timesheet' | 'utilisation' | 'billableHours') => boolean;
  canExportReport: (reportType: 'timesheet' | 'utilisation' | 'billableHours') => boolean;
  getReportViewAccessLevel: (reportType: 'timesheet' | 'utilisation' | 'billableHours') => AccessLevel;
  
  // System settings permissions
  canManageRolesPermissions: () => boolean;
  canManageSystemSettings: () => boolean;
  canViewAuditLogs: () => boolean;
  
  // Smart features permissions
  canUseAiAssistant: () => boolean;
  canUseAnomalyDetection: () => boolean;
  canUseScheduledBookings: () => boolean;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

// Interface for the API response when fetching current employee
interface CurrentEmployeeResponse {
  id: number;
  name: string;
  email: string;
  userRoleId?: number;
  userRoleName?: string;
  permissions?: UserRolePermissions;
}

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentEmployee } = useTimesheet();
  const { getScreenMode } = useDataSource();
  const [permissions, setPermissions] = useState<UserRolePermissions | null>(null);
  const [userRoleId, setUserRoleId] = useState<number | null>(null);
  const [userRoleName, setUserRoleName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Track if we've already fetched permissions from API to avoid duplicate calls
  const hasFetchedFromApi = useRef(false);

  // Function to fetch permissions from API (for live mode)
  const fetchPermissionsFromApi = useCallback(async () => {
    try {
      console.log('[PermissionContext] Fetching permissions from API...');
      const response = await apiClient.get<CurrentEmployeeResponse>('/employees/me');
      
      if (response.permissions) {
        console.log('[PermissionContext] Loaded permissions from API:', response.permissions);
        setPermissions(response.permissions);
        setUserRoleId(response.userRoleId ?? null);
        setUserRoleName(response.userRoleName ?? null);
      } else {
        console.log('[PermissionContext] No permissions in API response, using defaults');
        setPermissions(getDefaultPermissions());
        setUserRoleId(response.userRoleId ?? null);
        setUserRoleName(response.userRoleName ?? null);
      }
      hasFetchedFromApi.current = true;
    } catch (error) {
      console.error('[PermissionContext] Error fetching permissions from API:', error);
      // Fall back to defaults on error
      setPermissions(getDefaultPermissions());
      setUserRoleId(null);
      setUserRoleName(null);
    }
  }, []);

  // Function to reload permissions from localStorage (for demo mode)
  const reloadPermissionsFromStorage = useCallback(() => {
    if (!currentEmployee) return;
    
    const mode = getScreenMode('timesheet-grid');
    if (mode !== 'demo') return;
    
    // Check for currently editing role first
    const currentEditingRoleId = localStorage.getItem('currentEditingRoleId');
    if (currentEditingRoleId) {
      const roleId = parseInt(currentEditingRoleId, 10);
      const storedPermissions = loadRolePermissionsFromStorage(roleId);
      const roleName = getRoleNameFromStorage(roleId);
      if (storedPermissions) {
        console.log(`[PermissionContext] Reloading permissions for role ${roleId}`);
        setPermissions(storedPermissions);
        setUserRoleId(roleId);
        setUserRoleName(roleName);
      }
    }
  }, [currentEmployee, getScreenMode]);

  // Function to reload permissions based on current mode
  const reloadPermissions = useCallback(() => {
    const mode = getScreenMode('timesheet-grid');
    if (mode === 'live') {
      // In live mode, re-fetch from API
      hasFetchedFromApi.current = false; // Reset flag to allow re-fetch
      fetchPermissionsFromApi();
    } else {
      // In demo mode, reload from localStorage
      reloadPermissionsFromStorage();
    }
  }, [getScreenMode, fetchPermissionsFromApi, reloadPermissionsFromStorage]);

  // Listen for localStorage changes (when permissions are saved in EditRolePage)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key?.startsWith('userRolePermissions_') || event.key === 'currentEditingRoleId') {
        console.log('[PermissionContext] Detected localStorage change, reloading permissions');
        reloadPermissionsFromStorage();
      }
    };

    // Also listen for custom event dispatched when saving permissions
    const handlePermissionsUpdated = () => {
      console.log('[PermissionContext] Detected permissions update event, reloading');
      reloadPermissions(); // Use the mode-aware reload function
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('permissionsUpdated', handlePermissionsUpdated);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('permissionsUpdated', handlePermissionsUpdated);
    };
  }, [reloadPermissionsFromStorage, reloadPermissions]);

  // Load permissions from the current employee, API, or localStorage (demo mode)
  useEffect(() => {
    if (currentEmployee) {
      // Check if currentEmployee has permissions (from the updated API response in live mode)
      const empWithPermissions = currentEmployee as {
        userRoleId?: number;
        userRoleName?: string;
        permissions?: UserRolePermissions;
      };
      
      // Check the current mode first
      const mode = getScreenMode('timesheet-grid');
      
      if (empWithPermissions.permissions) {
        // Permissions available in currentEmployee (from BootstrapGate in production)
        console.log('[PermissionContext] Using permissions from currentEmployee');
        setPermissions(empWithPermissions.permissions);
        setUserRoleId(empWithPermissions.userRoleId ?? null);
        setUserRoleName(empWithPermissions.userRoleName ?? null);
        setIsLoading(false);
      } else if (mode === 'live') {
        // Live mode but no permissions in currentEmployee - fetch from API
        // This handles the case where BootstrapGate didn't run (development mode)
        // or when currentEmployee was set from demo data
        if (!hasFetchedFromApi.current) {
          console.log('[PermissionContext] Live mode: fetching permissions from API...');
          fetchPermissionsFromApi().finally(() => {
            setIsLoading(false);
          });
        } else {
          // Already fetched, just set loading to false
          setIsLoading(false);
        }
      } else {
        // Demo mode: try to load permissions from localStorage
        // First, check if employee has an assigned role in localStorage
        const assignedRoleId = getEmployeeRoleIdFromStorage(currentEmployee.id);
        if (assignedRoleId) {
          const storedPermissions = loadRolePermissionsFromStorage(assignedRoleId);
          const roleName = getRoleNameFromStorage(assignedRoleId);
          if (storedPermissions) {
            console.log(`[PermissionContext] Demo mode: loaded permissions for role ${assignedRoleId} (${roleName})`);
            setPermissions(storedPermissions);
            setUserRoleId(assignedRoleId);
            setUserRoleName(roleName);
          } else {
            console.log('[PermissionContext] Demo mode: no permissions found for assigned role, using defaults');
            setPermissions(getDefaultPermissions());
            setUserRoleId(assignedRoleId);
            setUserRoleName(roleName);
          }
        } else {
          // No role assigned - check if there's a "current role being edited" in localStorage
          // This allows testing permissions while editing a role
          const currentEditingRoleId = localStorage.getItem('currentEditingRoleId');
          if (currentEditingRoleId) {
            const roleId = parseInt(currentEditingRoleId, 10);
            const storedPermissions = loadRolePermissionsFromStorage(roleId);
            const roleName = getRoleNameFromStorage(roleId);
            if (storedPermissions) {
              console.log(`[PermissionContext] Demo mode: using permissions from currently editing role ${roleId}`);
              setPermissions(storedPermissions);
              setUserRoleId(roleId);
              setUserRoleName(roleName);
            } else {
              setPermissions(getDefaultPermissions());
              setUserRoleId(null);
              setUserRoleName(null);
            }
          } else {
            console.log('[PermissionContext] Demo mode: no role assigned, using defaults');
            setPermissions(getDefaultPermissions());
            setUserRoleId(null);
            setUserRoleName(null);
          }
        }
        setIsLoading(false);
      }
    }
  }, [currentEmployee, getScreenMode, fetchPermissionsFromApi]);

  // Helper to check access level
  const checkAccessLevel = useCallback((accessLevel: AccessLevel | undefined, targetEmployeeId: number): boolean => {
    if (!currentEmployee) return false;
    
    const level = accessLevel ?? 'own';
    switch (level) {
      case 'all':
        return true;
      case 'team':
        // Check if target is self or a direct report
        return targetEmployeeId === currentEmployee.id || 
               currentEmployee.managerId === targetEmployeeId;
      case 'own':
        return targetEmployeeId === currentEmployee.id;
      case 'none':
        return false;
      default:
        return targetEmployeeId === currentEmployee.id;
    }
  }, [currentEmployee]);

  // Timesheet permissions
  const canViewTimesheet = useCallback((targetEmployeeId: number): boolean => {
    return checkAccessLevel(permissions?.timesheetAccess?.viewAccess, targetEmployeeId);
  }, [permissions, checkAccessLevel]);

  const canEditTimesheet = useCallback((targetEmployeeId: number): boolean => {
    return checkAccessLevel(permissions?.timesheetAccess?.editAccess, targetEmployeeId);
  }, [permissions, checkAccessLevel]);

  const canDeleteTimesheet = useCallback((): boolean => {
    return permissions?.timesheetAccess?.allowDelete ?? false;
  }, [permissions]);

  const getTimesheetViewAccessLevel = useCallback((): AccessLevel => {
    return permissions?.timesheetAccess?.viewAccess ?? 'own';
  }, [permissions]);

  const getTimesheetEditAccessLevel = useCallback((): AccessLevel => {
    return permissions?.timesheetAccess?.editAccess ?? 'own';
  }, [permissions]);

  // Approval permissions
  const canViewApprovalDashboard = useCallback((): boolean => {
    return permissions?.approvalPermissions?.viewDashboard ?? false;
  }, [permissions]);

  const canViewApprovals = useCallback((): boolean => {
    return permissions?.timesheetApprovalPermissions?.viewApprovals ?? false;
  }, [permissions]);

  const canApproveReject = useCallback((): boolean => {
    // Backend uses editTimesheets and rejectTimesheets instead of approveReject
    return (permissions?.timesheetApprovalPermissions?.editTimesheets ?? false) ||
           (permissions?.timesheetApprovalPermissions?.rejectTimesheets ?? false);
  }, [permissions]);

  const canBulkApprove = useCallback((): boolean => {
    return permissions?.timesheetApprovalPermissions?.bulkApprove ?? false;
  }, [permissions]);

  const canDelegateApproval = useCallback((): boolean => {
    return permissions?.timesheetApprovalPermissions?.delegateApproval ?? false;
  }, [permissions]);

  // Report permissions
  const canViewReport = useCallback((reportType: 'timesheet' | 'utilisation' | 'billableHours'): boolean => {
    const reportPermissions = permissions?.reportAccessPermissions;
    if (!reportPermissions) return false;

    switch (reportType) {
      case 'timesheet':
        return reportPermissions.timesheetReport?.enabled ?? false;
      case 'utilisation':
        return reportPermissions.utilisationReport?.enabled ?? false;
      case 'billableHours':
        return reportPermissions.billableHoursReport?.enabled ?? false;
      default:
        return false;
    }
  }, [permissions]);

  const canExportReport = useCallback((reportType: 'timesheet' | 'utilisation' | 'billableHours'): boolean => {
    const reportPermissions = permissions?.reportAccessPermissions;
    if (!reportPermissions) return false;

    // Backend uses allowExport instead of exportAccess
    switch (reportType) {
      case 'timesheet':
        return reportPermissions.timesheetReport?.allowExport ?? false;
      case 'utilisation':
        return reportPermissions.utilisationReport?.allowExport ?? false;
      case 'billableHours':
        return reportPermissions.billableHoursReport?.allowExport ?? false;
      default:
        return false;
    }
  }, [permissions]);

  const getReportViewAccessLevel = useCallback((reportType: 'timesheet' | 'utilisation' | 'billableHours'): AccessLevel => {
    const reportPermissions = permissions?.reportAccessPermissions;
    if (!reportPermissions) return 'own';

    switch (reportType) {
      case 'timesheet':
        return reportPermissions.timesheetReport?.viewAccess ?? 'own';
      case 'utilisation':
        return reportPermissions.utilisationReport?.viewAccess ?? 'own';
      case 'billableHours':
        return reportPermissions.billableHoursReport?.viewAccess ?? 'own';
      default:
        return 'own';
    }
  }, [permissions]);

  // System settings permissions
  const canManageRolesPermissions = useCallback((): boolean => {
    return permissions?.systemSettingsPermissions?.manageRolesPermissions ?? false;
  }, [permissions]);

  const canManageSystemSettings = useCallback((): boolean => {
    return permissions?.systemSettingsPermissions?.manageGeneralSettings === 'configure';
  }, [permissions]);

  const canViewAuditLogs = useCallback((): boolean => {
    return permissions?.systemSettingsPermissions?.viewAuditLogs ?? false;
  }, [permissions]);

  // Smart features permissions
  // Backend uses different field names: enableAiInsights, etc.
  const canUseAiAssistant = useCallback((): boolean => {
    return permissions?.smartFeaturesPermissions?.enableAiInsights ?? true;
  }, [permissions]);

  const canUseAnomalyDetection= useCallback((): boolean => {
    // Backend uses enableAutoSmartFill for anomaly detection
    return permissions?.smartFeaturesPermissions?.enableAutoSmartFill ?? true;
  }, [permissions]);

  const canUseScheduledBookings = useCallback((): boolean => {
    // Backend uses enableCopyFromLastWeek as a proxy for scheduled bookings
    return permissions?.smartFeaturesPermissions?.enableCopyFromLastWeek ?? true;
  }, [permissions]);

  const value: PermissionContextType = {
    permissions,
    userRoleId,
    userRoleName,
    isLoading,
    canViewTimesheet,
    canEditTimesheet,
    canDeleteTimesheet,
    getTimesheetViewAccessLevel,
    getTimesheetEditAccessLevel,
    canViewApprovalDashboard,
    canViewApprovals,
    canApproveReject,
    canBulkApprove,
    canDelegateApproval,
    canViewReport,
    canExportReport,
    getReportViewAccessLevel,
    canManageRolesPermissions,
    canManageSystemSettings,
    canViewAuditLogs,
    canUseAiAssistant,
    canUseAnomalyDetection,
    canUseScheduledBookings,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

// Default permissions for users without a role assigned
function getDefaultPermissions(): UserRolePermissions {
  return {
    timesheetAccess: {
      viewAccess: 'own',
      editAccess: 'own',
      allowDelete: false,
    },
    smartFeaturesPermissions: {
      aiAssistant: true,
      anomalyDetection: true,
      scheduledBookings: true,
    },
    approvalPermissions: {
      viewDashboard: false,
      viewTeamMetrics: false,
      viewAllMetrics: false,
    },
    timesheetApprovalPermissions: {
      viewApprovals: false,
      approveReject: false,
      bulkApprove: false,
      delegateApproval: false,
    },
    reportAccessPermissions: {
      timesheetReport: { enabled: true, viewAccess: 'own', exportAccess: false },
      utilisationReport: { enabled: false, viewAccess: 'none', exportAccess: false },
      billableHoursReport: { enabled: false, viewAccess: 'none', exportAccess: false },
    },
    analyticsInsightsPermissions: {
      viewAnalytics: false,
      viewTrends: false,
      viewForecasts: false,
    },
    employeeDirectoryPermissions: {
      viewDirectory: 'own',
      viewContactInfo: false,
      viewWorkingPatterns: false,
    },
    integrationsPermissions: {
      viewIntegrations: false,
      manageIntegrations: false,
    },
    companyManagementPermissions: {
      viewCompanies: false,
      manageCompanies: false,
    },
    systemSettingsPermissions: {
      manageGeneralSettings: 'none',
      manageApprovalWorkflows: false,
      manageRolesPermissions: false,
      viewAuditLogs: false,
    },
  };
}
